import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../services/firebase";
import { redirectToCheckout } from "../services/stripe";
import { getTrialStatus } from "../utils/trialStatus";
import "./BillingPage.css";

const MONTHLY_PRICE_ID = process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID || "";

const copy = {
  checkout: {
    eyebrow: "Billing",
    title: "Keep ManuCalc access active.",
    body: "Start checkout only when your trial has ended or you are ready to subscribe. Founding members and company-seat users do not need billing.",
  },
  success: {
    eyebrow: "Billing Complete",
    title: "Your ManuCalc access is being confirmed.",
    body: "Stripe has returned you to ManuCalc. Webhooks normally update the subscription within a few seconds.",
  },
  cancel: {
    eyebrow: "Billing Cancelled",
    title: "Checkout was cancelled.",
    body: "No payment method was saved from this checkout attempt. You can return to ManuCalc if you still have access or restart checkout when ready.",
  },
  return: {
    eyebrow: "Billing Portal",
    title: "Welcome back from billing settings.",
    body: "Your account details are refreshing. If you changed a subscription, Stripe may take a few seconds to update ManuCalc.",
  },
};

const BillingPage = ({ user, accountProfile }) => {
  const location = useLocation();
  const mode = useMemo(() => {
    if (location.pathname.endsWith("/success")) return "success";
    if (location.pathname.endsWith("/cancel")) return "cancel";
    if (location.pathname.endsWith("/return")) return "return";
    return "checkout";
  }, [location.pathname]);
  const [status, setStatus] = useState("Checking billing state...");
  const [profile, setProfile] = useState(accountProfile || null);
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    const refresh = async () => {
      if (!user?.uid) {
        setStatus("Sign in to manage billing.");
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (!mounted) return;
        const data = snap.exists() ? snap.data() : {};
        setProfile(data);
        const trial = getTrialStatus(data.trialStartDate, data.trialEndDate);
        if (data.isFounder === true) {
          setStatus(`Founding Member access active${data.founderNumber ? ` (#${data.founderNumber})` : ""}.`);
        } else if (data.subscriptionStatus === "active" || data.subscriptionStatus === "trialing") {
          setStatus("Subscription access is active.");
        } else if (data.billingExempt || data.subscriptionRequired === false) {
          setStatus("Billing is not required for this account.");
        } else if (trial?.isTrialActive) {
          setStatus(`Trial access is still active: ${trial.shortFormattedTimeRemaining}.`);
        } else {
          setStatus("Billing is required to continue unrestricted access.");
        }
      } catch (refreshError) {
        console.error("Billing state refresh failed:", refreshError);
        if (mounted) setStatus("Billing state could not be refreshed. Try again in a moment.");
      }
    };

    refresh();
    const timer = window.setTimeout(refresh, 3500);
    return () => {
      mounted = false;
      window.clearTimeout(timer);
    };
  }, [user]);

  const hasAccess =
    profile?.isFounder === true ||
    profile?.subscriptionStatus === "active" ||
    profile?.subscriptionStatus === "trialing" ||
    profile?.billingExempt === true ||
    profile?.subscriptionRequired === false;

  const startCheckout = async () => {
    if (!user?.uid || !user?.email) {
      window.location.assign("/auth");
      return;
    }
    try {
      setLoadingCheckout(true);
      setError("");
      await redirectToCheckout({
        email: user.email,
        uid: user.uid,
        priceId: MONTHLY_PRICE_ID,
        accountType: profile?.accountBillingType || "personal",
      });
    } catch (checkoutError) {
      console.error("Checkout failed:", checkoutError);
      setError(checkoutError.message || "Checkout could not be started.");
      setLoadingCheckout(false);
    }
  };

  const content = copy[mode] || copy.checkout;

  return (
    <main className="billing-page">
      <section className="billing-card">
        <span>{content.eyebrow}</span>
        <h1>{content.title}</h1>
        <p>{content.body}</p>
        <div className={`billing-status ${hasAccess ? "active" : ""}`}>
          {status}
        </div>
        {error && <p className="billing-error">{error}</p>}
        <div className="billing-actions">
          {hasAccess ? (
            <Link to="/dashboard" className="btn btn-primary">Open ManuCalc</Link>
          ) : (
            <button type="button" className="btn btn-primary" onClick={startCheckout} disabled={loadingCheckout}>
              {loadingCheckout ? "Opening Checkout..." : "Start Billing"}
            </button>
          )}
          <Link to="/settings" className="btn btn-secondary">Account Settings</Link>
          <Link to="/" className="btn btn-secondary">Back Home</Link>
        </div>
      </section>
    </main>
  );
};

export default BillingPage;
